import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ScanLine, Share2, Copy, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import BottomNav from '../components/BottomNav';
import UserAvatar from '../components/UserAvatar';
import AuraScore from '../components/AuraScore';
import LevelProgress from '../components/LevelProgress';
import { getLevelForAura } from '../lib/aura';

export default function QRPage() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [privacy, setPrivacy] = useState(user?.battle_privacy || 'all');
  const token = user?.public_qr_token;

  const qrUrl = token ? `${window.location.origin}/scan#${token}` : '';
  const qrSrc = token
    ? `https://api.qrserver.com/v1/create-qr-code/?size=520x520&margin=1&color=00F0FF&bgcolor=0F0F16&data=${encodeURIComponent(qrUrl)}`
    : null;

  const level = getLevelForAura(user?.aura_points || 0);

  const copy = async () => {
    try { await navigator.clipboard.writeText(qrUrl); toast.success(t('qr.copied')); }
    catch { toast.error(t('common.error')); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Aura QR · @${user.username}`, text: t('qr.shareText'), url: qrUrl }); } catch {}
    } else { copy(); }
  };

  const setBattlePrivacy = async (val) => {
    setPrivacy(val);
    try {
      await api.patch('/qr/me/privacy', { battle_privacy: val });
      await refresh();
      toast.success(t('common.save'));
    } catch { toast.error(t('common.error')); }
  };

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-aura-cyan/25 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-aura-violet/25 blur-3xl" />

      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim hover:text-white flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} /> {t('common.back')}
        </button>
        <span className="font-display font-black text-lg tracking-[0.3em] aura-text-gradient">MI AURA QR</span>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-5">
        <section className="aura-card p-6 text-center relative overflow-hidden grain">
          <div className="mx-auto p-3 rounded-2xl bg-aura-surface2 border border-aura-cyan/40 inline-block shadow-neon-cyan">
            {qrSrc && <img src={qrSrc} alt="Aura QR" className="h-56 w-56 block" data-testid="my-qr-img" />}
          </div>

          <div className="mt-5 flex flex-col items-center gap-1">
            <UserAvatar user={user} size="md" />
            <div className="mt-2 font-display font-bold text-white text-lg" data-testid="qr-name">{user?.name}</div>
            <div className="text-aura-textDim text-sm" data-testid="qr-username">@{user?.username}</div>
            <div className="mt-3">
              <AuraScore points={user?.aura_points || 0} size="md" testId="qr-aura" />
            </div>
            <div className="mt-2 font-display font-black tracking-widest" style={{ color: level.color }}>
              {t(level.key)}
            </div>
          </div>

          <div className="mt-6">
            <LevelProgress points={user?.aura_points || 0} />
          </div>
        </section>

        {/* Actions */}
        <section className="grid grid-cols-3 gap-2">
          <Link to="/scan" data-testid="btn-scan" className="aura-btn-primary !py-3 !text-sm justify-center">
            <ScanLine size={16} /> {t('qr.scan')}
          </Link>
          <button onClick={share} data-testid="btn-share-qr" className="aura-btn-ghost !py-3 !text-sm justify-center">
            <Share2 size={14} /> {t('qr.share')}
          </button>
          <button onClick={copy} data-testid="btn-copy-qr" className="aura-btn-ghost !py-3 !text-sm justify-center">
            <Copy size={14} /> {t('qr.copyLink')}
          </button>
        </section>

        {/* Battle privacy */}
        <section className="aura-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} className="text-aura-cyan" />
            <div className="font-display font-bold text-white text-sm tracking-wider">{t('qr.privacyTitle')}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {['all', 'followers', 'following', 'none'].map((v) => (
              <button
                key={v}
                onClick={() => setBattlePrivacy(v)}
                data-testid={`privacy-${v}`}
                className={`rounded-xl py-2 text-xs font-display font-semibold border ${privacy === v ? 'border-aura-cyan text-aura-cyan' : 'border-white/10 text-white/80'}`}
              >
                {t(`qr.privacy.${v}`)}
              </button>
            ))}
          </div>
        </section>
      </main>
      <BottomNav />
    </div>
  );
}
